<template>
	<div v-if="shouldShowLanguageTable" class="mt-4">
		<h2 class="text-left">
			{{ $t(`language.languages`) }}
		</h2>
		<div class="mb-4 mobile-overflow-table">
			<table class="table mb-0 table-striped" :key="$i18n.locale">
				<thead class="thead-dark">
					<tr>
						<th v-if="match"></th>
						<th class="w-34">
							{{ $t(`language.language`) }}
						</th>
						<th v-if="match" class="w-33">
							{{ $t('general.job-offer') }}
						</th>
						<th v-else class="w-33">
							{{ $t('languages.niveau-title') }}
						</th>
						<th v-if="match" class="w-33">
							{{ $t('general.applicant-profile') }}
						</th>
					</tr>
				</thead>
				<tbody v-if="match">
					<tr
						v-for="lang in sortedLanguages.matched"
						:key="'language' + lang.id"
						v-if="
							sortedLanguages.matched &&
								sortedLanguages.matched.length > 0
						"
					>
						<th :class="getLanguageMatch(lang.id)" width="50">
							<Icon class="w-5" :icon="['far', 'check']" />
						</th>
						<th>{{ lang[$getLocaleDbColumn('name')] }}</th>
						<td>{{ translateNiveau(lang.niveau) }}</td>
						<td>{{ translateNiveau(lang.niveauApplicant) }}</td>
					</tr>
					<tr
						v-for="lang in sortedLanguages.jobOnly"
						:key="'language' + lang.id"
						v-if="
							sortedLanguages.jobOnly &&
								sortedLanguages.jobOnly.length > 0
						"
					>
						<th :class="getLanguageMatch(lang.id)" width="50">
							<Icon class="w-5" :icon="['far', 'times']" />
						</th>
						<th>{{ lang[$getLocaleDbColumn('name')] }}</th>
						<td>{{ translateNiveau(lang.niveau) }}</td>
						<td>-</td>
					</tr>
					<tr
						v-if="
							sortedLanguages.applicantOnly &&
								sortedLanguages.applicantOnly.length > 0
						"
					>
						<td></td>
						<td colspan="3">
							{{ translateNiveau('other-languages') }}
						</td>
					</tr>
					<tr
						v-for="lang in sortedLanguages.applicantOnly"
						:key="'language' + lang.id"
						v-if="
							sortedLanguages.applicantOnly &&
								sortedLanguages.applicantOnly.length > 0
						"
					>
						<th></th>
						<th>{{ lang[$getLocaleDbColumn('name')] }}</th>
						<td>-</td>
						<td>{{ translateNiveau(lang.niveau) }}</td>
					</tr>
				</tbody>
				<tbody
					v-else-if="
						sortedLanguages.jobOnly &&
							sortedLanguages.jobOnly.length > 0
					"
				>
					<tr v-for="lang in sortedLanguages.jobOnly" :key="lang.id">
						<td>{{ lang[$getLocaleDbColumn('name')] }}</td>
						<td>{{ translateNiveau(lang.niveau) }}</td>
					</tr>
				</tbody>
				<tbody
					v-else-if="
						sortedLanguages.applicantOnly &&
							sortedLanguages.applicantOnly.length > 0
					"
				>
					<tr
						v-for="lang in sortedLanguages.applicantOnly"
						:key="lang.id"
					>
						<td>{{ lang[$getLocaleDbColumn('name')] }}</td>
						<td>{{ translateNiveau(lang.niveau) }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import Icon from '~/components/ui/common/Icon'

export default {
	components: {
		Icon
	},
	props: ['jobOffer', 'match', 'applicantProfile', 'hideEmptyJobOfferTables'],
	data: () => ({
		collapse: false
	}),
	computed: {
		shouldShowLanguageTable() {
			if (!this.sortedLanguages) return false
			return !this.hideEmptyJobOfferTables || this.hasLanguageRows
		},
		hasLanguageRows() {
			if (!this.sortedLanguages) {
				return false
			}

			return ['matched', 'jobOnly', 'applicantOnly'].some(group => {
				return (
					this.sortedLanguages[group] &&
					this.sortedLanguages[group].length > 0
				)
			})
		},
		sortedLanguages() {
			if (!this.jobOffer && !this.applicantProfile) return null
			if (!this.jobOffer) {
				// Normalize applicant languages even when there's no job offer
				const applicantLanguages = this.applicantProfile.languages || []
				const normalizedApplicantLanguages = applicantLanguages.map(lang => {
					const niveau =
						lang.pivot && lang.pivot.niveau
							? lang.pivot.niveau
							: lang.niveau || 'elementary'
					return {
						...lang,
						niveau: niveau
					}
				})
				return {
					applicantOnly: normalizedApplicantLanguages
				}
			}
			if (!this.applicantProfile) {
				// Normalize job-offer languages when there's no applicant profile
				const normalizedJobLanguages = (this.jobOffer.languages || []).map(
					lang => {
						const niveau =
							lang.pivot && lang.pivot.niveau
								? lang.pivot.niveau
								: lang.niveau || 'elementary'
						return {
							...lang,
							niveau
						}
					}
				)
				return {
					jobOnly: normalizedJobLanguages
				}
			}
			return this.getSortedLanguages(
				this.jobOffer.languages || [],
				this.applicantProfile.languages || []
			)
		}
	},
	methods: {
		getLanguageMatch(language) {
			const languages =
				this.match && this.match.details && this.match.details.languages
			// APR-1155: if the payload arrives as a 0-indexed array the
			// original language IDs are lost; treat it as no match data
			// rather than producing wrong-colored checkmarks.
			if (!languages || Array.isArray(languages)) return undefined
			return languages[language] ? 'matched' : 'not-matched'
		},

		getSortedLanguages(jobOfferLanguages, applicantProfileLanguages) {
			const intersection = jobOfferLanguages.filter(lang =>
				applicantProfileLanguages.map(l => l.id).includes(lang.id)
			)
			const jobOnly = jobOfferLanguages.filter(
				lang =>
					!applicantProfileLanguages.map(l => l.id).includes(lang.id)
			)
			const applicantOnly = applicantProfileLanguages.filter(
				lang => !jobOfferLanguages.map(l => l.id).includes(lang.id)
			)

			// Normalize applicantOnly languages to ensure niveau is properly set
			const normalizedApplicantOnly = applicantOnly.map(lang => {
				const niveau =
					lang.pivot && lang.pivot.niveau
						? lang.pivot.niveau
						: lang.niveau || 'elementary'
				return {
					...lang,
					niveau: niveau
				}
			})

			// Normalize jobOnly languages (niveau may be on pivot)
			const normalizedJobOnly = jobOnly.map(lang => {
				const niveau =
					lang.pivot && lang.pivot.niveau
						? lang.pivot.niveau
						: lang.niveau || 'elementary'
				return {
					...lang,
					niveau
				}
			})

			return {
				matched: intersection.map(lang => {
					const applicantLang = applicantProfileLanguages.find(
						l => l.id === lang.id
					)
					const applicantNiveau =
						applicantLang.pivot && applicantLang.pivot.niveau
							? applicantLang.pivot.niveau
							: applicantLang.niveau || 'elementary'
					const jobNiveau =
						lang.pivot && lang.pivot.niveau
							? lang.pivot.niveau
							: lang.niveau || 'elementary'
					return {
						...lang,
						niveau: jobNiveau,
						niveauApplicant: applicantNiveau
					}
				}),
				jobOnly: normalizedJobOnly,
				applicantOnly: normalizedApplicantOnly
			}
		},

		translateNiveau(niveau) {
			if (!niveau) {
				return '-'
			}
			const key = `languages.niveaus.${niveau}`
			// Vue-i18n: check if the key exists for current locale
			// First, check if the key exists for the current (possibly regional) locale
			if (this.$i18n.te(key, this.$i18n.locale)) {
				return this.$t(key)
			}
			// Then try base locale (e.g., 'de' part of 'de-CH')
			const baseLocale = this.$i18n.locale.split('-')[0]
			if (baseLocale && this.$i18n.te(key, baseLocale)) {
				return this.$i18n.t(key, baseLocale)
			}
			// default: capitalised raw value
			return niveau.charAt(0).toUpperCase() + niveau.slice(1)
		}
	}
}
</script>
